import { z } from "zod";
import {
  ASSIGN_TO,
  START_DATE,
  END_DATE,
  NAME,
  NOTES,
  PRIORITY,
  ROLE,
  SCHOOL,
  TASK_ATTACHMENT,
  TASK_STATUS,
  TASKS,
  ASSIGNEES,
  TASK_ATTACHMENT_NAME,
} from "../consts";

// Step 1️⃣: Define base schema (without refine)
const baseTaskSchema = z.object({
  [START_DATE]: z.string().min(1, "Start Date is required"),
  [END_DATE]: z.string().min(1, "End Date is required"),
  [TASK_STATUS]: z.string().min(1, "Task Status is required"),
  [TASK_ATTACHMENT]: z.union([z.string(), z.instanceof(File)]).optional(),
  [TASK_ATTACHMENT_NAME]: z.string().optional(),
  [ASSIGNEES]: z
    .array(
      z.object({
        [SCHOOL]: z.string().optional(),
        [ROLE]: z.string().optional(),
        [ASSIGN_TO]: z.string().min(1, "Assign To is required"),
      })
    )
    .min(1, "At least one assignee is required"),
});

// Step 2️⃣: Add refinement (creates a ZodEffects)
const validatedBaseTaskSchema = baseTaskSchema
  .refine(
    (data) => {
      const start = new Date(data[START_DATE]);
      const end = new Date(data[END_DATE]);
      return !isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start;
    },
    {
      message: "End Date cannot be earlier than Start Date",
      path: [END_DATE],
    }
  )
  .superRefine((data, ctx) => {
    // Check for duplicate ASSIGN_TO values
    const assignToValues = data[ASSIGNEES].map((assignee: any) => assignee[ASSIGN_TO]);
    const seen = new Map<string, number>();
    
    assignToValues.forEach((value: string, index: number) => {
      if (value && value.trim() !== "") {
        if (seen.has(value)) {
          // Mark the duplicate occurrence with error
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This person is already assigned. Please select a different person.",
            path: [ASSIGNEES, index, ASSIGN_TO],
          });
        } else {
          seen.set(value, index);
        }
      }
    });
  });

// Step 3️⃣: Extend based on validated schema
const newTaskSchema = validatedBaseTaskSchema.and(
  z.object({
    [NAME]: z.string().min(3, "Valid Task Name is required"),
    [PRIORITY]: z.string().min(1, "Priority is required"),
    [NOTES]: z.string().min(10, "Valid Task Notes are required"),
  })
);

const predefinedTaskSchema = validatedBaseTaskSchema.and(
  z.object({
    [NAME]: z.string().optional(),
    [PRIORITY]: z.string().optional(),
    [NOTES]: z.string().optional(),
  })
);

// Step 4️⃣: Main schema
export const schema = z.discriminatedUnion("taskType", [
  z.object({
    taskType: z.literal("new"),
    selectedCategory: z.string().optional(),
    [TASKS]: z.array(newTaskSchema),
  }),
  z.object({
    taskType: z.literal("predefined"),
    selectedCategory: z
      .string()
      .min(1, "Category is required for predefined tasks"),
    [TASKS]: z.array(predefinedTaskSchema),
  }),
]);

export type formValues = z.infer<typeof schema>;
